import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function drift(points) {
  if (points.length < 4) return null;
  const n = Math.max(1, Math.floor(points.length / 3));
  const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const first = avg(points.slice(0, n).map((p) => p.value));
  const last = avg(points.slice(-n).map((p) => p.value));
  return first ? ((last - first) / first) * 100 : null;
}

function Stats({ points, target, unit = "g", testId }) {
  if (points.length === 0) return <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>Sem pesagens registadas.</p>;
  const vals = points.map((p) => p.value);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  const d = drift(points);
  const DriftIcon = d === null ? Minus : d > 0.5 ? TrendingUp : d < -0.5 ? TrendingDown : Minus;
  const driftColor = d === null ? "text-slate-500" : Math.abs(d) > 3 ? "text-red-600" : Math.abs(d) > 1 ? "text-amber-600" : "text-emerald-600";
  return (
    <div className="grid grid-cols-5 gap-2 text-center text-xs" data-testid={`${testId}-stats`}>
      <div className="bg-slate-50 rounded p-2"><p className="text-muted-foreground">N</p><p className="font-bold">{vals.length}</p></div>
      <div className="bg-slate-50 rounded p-2"><p className="text-muted-foreground">Média</p><p className="font-bold">{mean.toFixed(2)} {unit}</p></div>
      <div className="bg-slate-50 rounded p-2"><p className="text-muted-foreground">Mín / Máx</p><p className="font-bold">{Math.min(...vals).toFixed(2)} / {Math.max(...vals).toFixed(2)}</p></div>
      <div className="bg-slate-50 rounded p-2"><p className="text-muted-foreground">vs Nominal</p><p className="font-bold">{target ? `${(((mean - target) / target) * 100).toFixed(1)}%` : "-"}</p></div>
      <div className="bg-slate-50 rounded p-2"><p className="text-muted-foreground">Deriva</p><p className={`font-bold flex items-center justify-center gap-1 ${driftColor}`} data-testid={`${testId}-drift`}><DriftIcon className="h-3.5 w-3.5" />{d === null ? "n/d" : `${d > 0 ? "+" : ""}${d.toFixed(1)}%`}</p></div>
    </div>
  );
}

function Chart({ points, target, min, max, color, testId }) {
  if (points.length === 0) return null;
  const domainVals = [...points.map((p) => p.value), target, min, max].filter((v) => v != null);
  const pad = (Math.max(...domainVals) - Math.min(...domainVals)) * 0.25 || 1;
  return (
    <div data-testid={testId}>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={points} margin={{ top: 10, right: 60, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
          <YAxis domain={[Math.min(...domainVals) - pad, Math.max(...domainVals) + pad]} tick={{ fontSize: 10 }} tickFormatter={(v) => v.toFixed(1)} />
          <Tooltip formatter={(v, n, p) => [`${v} g`, p.payload.source === "shift_verification" ? "Peso Turno" : "Pesagem"]} />
          {max != null && <ReferenceLine y={max} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Máx", position: "right", fontSize: 10, fill: "#dc2626" }} />}
          {target != null && <ReferenceLine y={target} stroke="#16a34a" strokeDasharray="2 2" label={{ value: "Nominal", position: "right", fontSize: 10, fill: "#16a34a" }} />}
          {min != null && <ReferenceLine y={min} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Mín", position: "right", fontSize: 10, fill: "#dc2626" }} />}
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={(p) => {
            const out = (min != null && p.payload.value < min) || (max != null && p.payload.value > max);
            return <circle key={p.index} cx={p.cx} cy={p.cy} r={out ? 5 : 3} fill={out ? "#dc2626" : color} stroke="#fff" strokeWidth={1} />;
          }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export const WeightHistoryDialog = ({ open, onOpenChange, orderId, orderCode }) => {
  const [loading, setLoading] = useState(false);
  const [piece, setPiece] = useState([]);
  const [shot, setShot] = useState([]);
  const [limits, setLimits] = useState({});

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/weight-records?order_id=${orderId}`),
      axios.get(`${API}/shift-weight-verification?order_id=${orderId}`).catch(() => ({ data: [] })),
    ]).then(([wr, sv]) => {
      const records = wr.data || [];
      const piecePts = [
        ...records.filter((r) => r.weight > 0).map((r) => ({ t: r.recorded_at, value: r.weight, source: "weight_record" })),
        ...(sv.data || []).filter((v) => v.weight_1_piece > 0 || v.weight_10_pieces > 0).map((v) => ({ t: v.recorded_at, value: v.weight_10_pieces > 0 ? +(v.weight_10_pieces / 10).toFixed(3) : v.weight_1_piece, source: "shift_verification" })),
      ].sort((a, b) => a.t.localeCompare(b.t)).map((p) => ({ ...p, label: fmtTime(p.t) }));
      const shotPts = records.filter((r) => r.shot_weight > 0).map((r) => ({ t: r.recorded_at, value: r.shot_weight, source: "weight_record" }))
        .sort((a, b) => a.t.localeCompare(b.t)).map((p) => ({ ...p, label: fmtTime(p.t) }));
      const latest = records[0] || {};
      setLimits({
        target: latest.weight_target, min: latest.weight_min, max: latest.weight_max,
        shotTarget: latest.shot_weight_target, shotMin: latest.shot_weight_min, shotMax: latest.shot_weight_max,
      });
      setPiece(piecePts); setShot(shotPts);
    }).finally(() => setLoading(false));
  }, [open, orderId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="weight-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo">Histórico de Pesagens — {orderCode}</DialogTitle>
          <DialogDescription>Evolução do peso ao longo da ordem. Pontos vermelhos = fora dos limites. Deriva compara o início com o fim da série.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <div className="space-y-6">
            <section className="space-y-2">
              <h3 className="font-semibold text-sm">Peso de 1 peça (g){limits.target ? ` · nominal ${limits.target} g` : ""}</h3>
              <Stats points={piece} target={limits.target} testId="piece" />
              <Chart points={piece} target={limits.target} min={limits.min} max={limits.max} color="#0b1f3a" testId="piece-chart" />
            </section>
            <section className="space-y-2">
              <h3 className="font-semibold text-sm">Peso da moldação completa com gito (g){limits.shotTarget ? ` · nominal ${limits.shotTarget} g` : ""}</h3>
              <Stats points={shot} target={limits.shotTarget} testId="shot" />
              <Chart points={shot} target={limits.shotTarget} min={limits.shotMin} max={limits.shotMax} color="#0d9488" testId="shot-chart" />
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
